/**
 * TriggerRuleEditorForm — the inline form body shared between
 * `TriggerRuleEditorModal` (workspace / template-centric surfaces) and
 * the per-variant trigger-rule drawer in the App Integrations tab (#1184,
 * Slice 1 of meta #1183).
 *
 * **Why this split.** Pre-#1184 the editor's form body lived inside
 * `TriggerRuleEditorModal` as private JSX. Slice 1 inlines the editor
 * into the variant-trigger-rule drawer (the drawer hosts the list + the
 * editor; no nested modal). The two existing modal surfaces
 * (`TriggerRulesForRepo`, `TriggersSection`) still want the modal
 * shell. Extracting the body keeps one validation + persistence
 * implementation; the modal becomes a thin wrapper that renders this
 * form inside `DialogContent`.
 *
 * The drawer instance presents two inputs only — **Suite selection**
 * (required, first; the UI label for what the code calls a "Replay
 * RunTemplate") and **Environment** (optional, second). It deliberately
 * suppresses the larger info callout the modal renders so the drawer
 * body stays scannable; the rule-fire description lives in the drawer
 * header.
 *
 * Code identifier note: "Suite selection" is the UI label. The
 * underlying entity is `RunTemplate` with `kind: 'replay'` — code
 * identifiers don't change.
 */
import { useEffect, useState } from 'react'
import { Info, FileQuestion } from 'lucide-react'
import {
  Button,
  Label,
  Input,
  SearchableSelect,
} from '@/components/ui'
import {
  triggerRulesAPI,
  apiFetch,
  API_BASE,
  type CreateTriggerRuleInput,
} from '@/lib/api'
import { requireActiveWorkspaceId } from '@/lib/workspace'
import type { TriggerRule, TriggerRuleProvider } from '@/types'

// ── Props ─────────────────────────────────────────────────────────────────

export interface TriggerRuleEditorFormProps {
  /** Existing rule to edit. Null when creating. */
  initialRule?: TriggerRule | null
  /** Pre-fill from the repo-centric / app-binding surface. Required
   * when `initialRule` is absent — without it the create call can't
   * build a CreateTriggerRuleInput. */
  presetRepo?: {
    integrationId: string
    repoIdentifier: string
    provider: TriggerRuleProvider
  }
  /** Pre-fill from the template-centric surface (used by
   * `TriggersSection`). When present, the template picker still renders
   * — the operator can change which template fires the rule. */
  presetTemplate?: {
    id: string
    name: string
    variantId: string
  }
  /** When true, the editor renders in its compact "drawer" shape: no
   * info callout, no template-empty-state hint card (the drawer
   * controls its own empty states). Default false — the modal callers
   * keep the verbose shape. */
  compact?: boolean
  /** Customer-facing label for the template picker. Default "Run with
   * template" matches the modal. The drawer passes "Suite selection"
   * per the #1184 UI vocabulary switch. */
  templateLabel?: string
  /** When true (drawer use case), the template field renders as
   * required — adds an asterisk + a required-validation hint when
   * empty + save touched. */
  templateRequired?: boolean
  /** Label for the save button. Default "Save trigger rule". */
  saveLabel?: string
  /** Pre-filtered list of templates. When provided, skips the network
   * fetch and uses this set as-is. Use when the caller already has the
   * variant-scoped templates loaded (the drawer does). */
  templateOptionsOverride?: Array<{ value: string; label: string }>
  onCancel: () => void
  onSaved: (rule: TriggerRule) => void
}

// ── Helpers ───────────────────────────────────────────────────────────────

function resolveProvider(
  presetRepo: TriggerRuleEditorFormProps['presetRepo'],
  initialRule: TriggerRule | null | undefined,
): TriggerRuleProvider | null {
  if (presetRepo) return presetRepo.provider
  if (initialRule) return initialRule.provider
  return null
}

/** Providers the editor renders a form for today. Other providers fall
 * through to the unsupported-provider placeholder.
 *
 * Mirrors the SUPPORTED_PROVIDERS set previously colocated with the
 * modal — kept here because the form is now the implementation of
 * record. */
const SUPPORTED_PROVIDERS: ReadonlySet<TriggerRuleProvider> = new Set(['github'])

// ── Component ─────────────────────────────────────────────────────────────

export function TriggerRuleEditorForm({
  initialRule,
  presetRepo,
  presetTemplate,
  compact = false,
  templateLabel = 'Run with template',
  templateRequired = false,
  saveLabel = 'Save trigger rule',
  templateOptionsOverride,
  onCancel,
  onSaved,
}: TriggerRuleEditorFormProps) {
  const provider = resolveProvider(presetRepo, initialRule)
  const providerSupported = provider !== null && SUPPORTED_PROVIDERS.has(provider)

  const [environment, setEnvironment] = useState(initialRule?.environment ?? '')
  const [templateId, setTemplateId] = useState(
    initialRule?.templateId ?? presetTemplate?.id ?? '',
  )
  const [templateOptions, setTemplateOptions] = useState<Array<{ value: string; label: string }>>(
    templateOptionsOverride ?? [],
  )
  const [templatesLoading, setTemplatesLoading] = useState(!templateOptionsOverride)

  useEffect(() => {
    if (templateOptionsOverride) {
      setTemplateOptions(templateOptionsOverride)
      setTemplatesLoading(false)
      return
    }
    async function load() {
      setTemplatesLoading(true)
      try {
        const wsId = requireActiveWorkspaceId()
        const resp = await apiFetch(`${API_BASE}/workspaces/${wsId}/replay-templates`)
        if (!resp.ok) throw new Error('Failed to fetch templates')
        const data = (await resp.json()) as Array<{ id?: string; _id?: string; name: string }>
        setTemplateOptions(
          data.map((t) => ({
            value: String(t.id ?? t._id ?? ''),
            label: t.name,
          })),
        )
      } catch {
        setTemplateOptions([])
      } finally {
        setTemplatesLoading(false)
      }
    }
    void load()
  }, [templateOptionsOverride])

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saveAttempted, setSaveAttempted] = useState(false)

  // Validation: must have a template AND a supported provider.
  // Environment is optional — blank means "fire on every successful
  // deploy", per the field help text.
  const canSave = !!templateId && providerSupported && !saving

  async function handleSave() {
    setSaveAttempted(true)
    if (!canSave || !provider) return
    setSaving(true)
    setError(null)
    try {
      const trimmedEnvironment = environment.trim()
      if (initialRule) {
        const patch = {
          templateId,
          environment: trimmedEnvironment || null,
        }
        const saved = await triggerRulesAPI.update(initialRule.id, patch)
        onSaved(saved)
      } else {
        if (!presetRepo) {
          setError('Cannot create a rule without a preset repo.')
          return
        }
        const input: CreateTriggerRuleInput = {
          provider,
          integrationId: presetRepo.integrationId,
          repoIdentifier: presetRepo.repoIdentifier,
          templateId,
          eventType: 'deployment_status',
          ...(trimmedEnvironment ? { environment: trimmedEnvironment } : {}),
        }
        const saved = await triggerRulesAPI.create(input)
        onSaved(saved)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save trigger rule')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Non-GitHub provider placeholder — signals provider-aware design */}
      {provider && !providerSupported && (
        <div className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
          <strong>Provider not yet supported in the UI.</strong> Trigger rules for{' '}
          <code className="font-mono text-xs">{provider}</code> will be manageable here
          once support lands.
        </div>
      )}

      {providerSupported && !compact && (
        <div
          className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 p-3 text-xs text-foreground"
          data-testid="trigger-info-callout"
        >
          <Info className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
          <p>
            Fires when your deployment platform reports a successful deploy
            to GitHub. OttoTester runs against the deploy's URL from the event
            payload, not the variant's default URL. Works automatically with
            Vercel, Netlify, Cloudflare Pages, AWS Amplify, Render, and any
            GHA workflow that uses GitHub Environments.
          </p>
        </div>
      )}

      {/* Template picker — rendered FIRST (per the #1184 mockup: Suite
        * selection is the required, primary input); Environment follows
        * as the optional, secondary input. The two modal callers
        * (workspace repo-centric + ReplayTemplateDialog's template-
        * centric Triggers section) inherit the same template-first /
        * environment-second order since they mount this form directly
        * — no per-caller field reorder. */}
      {providerSupported && (
        <div className="space-y-2">
          <Label className="text-sm font-semibold" htmlFor="trigger-template-picker">
            {templateLabel}
            {templateRequired && (
              <span className="ml-1 text-destructive font-semibold" aria-hidden="true">
                *
              </span>
            )}
          </Label>
          {!compact && (
            <p className="text-xs text-muted-foreground flex items-start gap-1">
              <Info className="h-3 w-3 mt-0.5 shrink-0" />
              Only Replay templates can be tied to trigger rules. Generation and
              Resume templates don't appear in this picker.
            </p>
          )}
          {templatesLoading ? (
            <div className="h-9 rounded-md border border-border bg-muted animate-pulse" />
          ) : templateOptions.length === 0 ? (
            compact ? (
              // Compact (drawer) empty state — a tight one-liner. The
              // modal renders the bordered card variant below; the drawer
              // doesn't because the drawer body owns the larger empty
              // states.
              <p
                data-testid="trigger-empty-state"
                className="text-xs text-muted-foreground"
              >
                No Replay templates yet. Create one from a variant's Start
                page (Replay → New) and come back here.
              </p>
            ) : (
              <div
                data-testid="trigger-empty-state"
                className="rounded-md border border-dashed border-border bg-muted/30 p-4 flex items-start gap-3"
              >
                <FileQuestion className="h-5 w-5 mt-0.5 shrink-0 text-muted-foreground" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">No Replay templates yet</p>
                  <p className="text-xs text-muted-foreground">
                    Open a variant's Start page and click{' '}
                    <strong>Replay → New</strong> to create one. New variants
                    also ship with a default Replay template you can use
                    immediately.
                  </p>
                </div>
              </div>
            )
          ) : (
            <SearchableSelect
              id="trigger-template-picker"
              options={templateOptions}
              value={templateId}
              onChange={setTemplateId}
              placeholder="— Pick one —"
            />
          )}
          {templateRequired && saveAttempted && !templateId && (
            <p className="text-xs text-destructive" role="alert">
              Required. Pick a suite to run when the rule fires.
            </p>
          )}
        </div>
      )}

      {/* Environment filter — optional. Blank fires on every deploy. */}
      {providerSupported && (
        <div className="space-y-2">
          <Label className="text-sm font-semibold" htmlFor="trigger-environment-input">
            Environment{' '}
            <span className="font-normal text-muted-foreground">(optional)</span>
          </Label>
          {!compact && (
            <p className="text-xs text-muted-foreground">
              Match the deployment's{' '}
              <code className="font-mono text-[11px]">environment</code> label.
              Leave blank to fire on every successful deploy.
            </p>
          )}
          <Input
            id="trigger-environment-input"
            value={environment}
            onChange={(e) => setEnvironment(e.target.value)}
            placeholder="e.g. Preview or Production"
            className="font-mono text-sm"
          />
          {compact && (
            <p className="text-[11px] text-muted-foreground">
              Optional. Leave blank to fire on every deploy.
            </p>
          )}
        </div>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}

      <div className="flex items-center justify-end gap-2 pt-2">
        {/* Disabled-Save reason hint — surfaces WHY the button is
          * inactive so the user has a concrete next action rather than
          * a silent grey button. Two reasons gate Save post-#1174: an
          * unsupported provider (the placeholder above is the
          * next-action read), or a missing template. The template hint
          * surfaces inline when the provider is fine but no template
          * is picked. Suppressed in the drawer (`templateRequired`)
          * because the asterisk + the inline required-field error
          * carry the same signal in the more compact shape. */}
        {!canSave && !saving && providerSupported && !templateId && !templateRequired && (
          <p className="text-xs text-muted-foreground mr-auto self-center">
            Pick a Replay template to enable Save
          </p>
        )}
        <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave} disabled={!canSave}>
          {saving ? 'Saving…' : saveLabel}
        </Button>
      </div>
    </div>
  )
}
